import { ChevronRight as ChevronRightIcon } from "@/assets/icons/24/ChevronRight";
import { useAuth } from "@/auth";
import {
  MegaMenuItemData,
  MengaMenuSubMenuType,
} from "@/common/contracts/mega-menu.contracts";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import { SEPARATOR_TAG } from "@/common/static/mega-menu.static";
import { ACCOUNT_ROUTE } from "@/common/static/routes.static";
import { cn } from "@/common/utils/css.utils";
import { MegaMenuMobileAccountItem } from "@/components/mega-menu/mobile/MegaMenuMobileAccountItem";
import { MegaMenuMobileActionDelegator } from "@/components/mega-menu/mobile/MegaMenuMobileActionDelegator";
import {
  MegaMenuMobileItem,
  MegaMenuMobileItemIcon,
} from "@/components/mega-menu/mobile/MegaMenuMobileItem";
import { MegaMenuMobilePanel } from "@/components/mega-menu/mobile/MegaMenuMobilePanel";
import { MegaMenuMobileSeparatorItem } from "@/components/mega-menu/mobile/MegaMenuMobileSeparatorItem";
import { ProBuyerMobileMenuItem } from "@/features/pro-buyer-application/components/ProBuyerHeaderLink";
import { PRO_BUYER_MENU_ITEM_ID } from "@/features/pro-buyer-application/utils/pro-buyer-nav.utils";
import { NewLabel } from "@/ui/cva/NewLabel";
import { Flavor } from "@/ui/typography/Flavor";

export const MegaMenuMobileItemList = ({
  items,
  type,
  onItemClick,
}: {
  items?: MegaMenuItemData[];
  type?: MengaMenuSubMenuType | null;
  onItemClick?: () => void;
}) => {
  const { isLoggedIn } = useAuth();

  const handleSubMenuClick = (item: MegaMenuItemData) => () => {
    /** Trigger `navigation_menu` event. */
    analyticsService.trackNavigationMenu(item.label);
  };

  return (
    <ul
      className={cn(
        "mb-4 flex flex-col",
        type?.startsWith("card") ? "space-y-2" : "space-y-4"
      )}
    >
      {items?.map(item => {
        if (item.label === SEPARATOR_TAG) {
          return <MegaMenuMobileSeparatorItem key={item.id} />;
        }

        /**
         * WHO-5453 round 4, F15. A logged-in user gets the grouped account
         * menu (same groups, labels and footer as the desktop sidebar) instead
         * of the flat Account submenu from the CMS JSON. Guests keep the JSON
         * items: Log in and Sign up.
         */
        if (isLoggedIn && item.href === ACCOUNT_ROUTE) {
          return (
            <MegaMenuMobileAccountItem
              key={item.id}
              item={item}
              onClick={handleSubMenuClick(item)}
              onClose={onItemClick}
            />
          );
        }

        if (item.id === PRO_BUYER_MENU_ITEM_ID) {
          return <ProBuyerMobileMenuItem key={item.id} onClick={onItemClick} />;
        }

        if (item.href?.includes("@")) {
          return (
            <MegaMenuMobileActionDelegator
              key={item.id}
              item={item}
              onClick={onItemClick}
            />
          );
        }

        const hasSubItems = !!item.items?.length || !!item.submenu;

        if (hasSubItems) {
          return (
            <MegaMenuMobilePanel
              key={item.id}
              trigger={
                <button
                  className="flex items-center space-x-2 text-left"
                  onClick={handleSubMenuClick(item)}
                >
                  <MegaMenuMobileItemIcon item={item} />
                  <Flavor className="flex-1">
                    {item.label}
                    {item.badge && (
                      <NewLabel className="ml-2">{item.badge}</NewLabel>
                    )}
                  </Flavor>
                  <ChevronRightIcon />
                </button>
              }
              type={item.submenuType}
              title={item.label}
              items={item.submenu?.items ?? item.items}
              hasBack={true}
              onClose={onItemClick}
            />
          );
        }

        return (
          <MegaMenuMobileItem key={item.id} item={item} onClick={onItemClick} />
        );
      })}
    </ul>
  );
};
